import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from './LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SiteContentContext = createContext(null);

// Language keys look like 'en', 'ko', 'fr', 'zh' etc.
const LANG_CODE_RE = /^[a-z]{2}$/;
const CORE_LANG_KEYS = ['en', 'ko', 'ja'];

// A multilingual text field: every key is a 2-letter code and at least one core language is present
const isMultilingualField = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length === 0) return false;
  return keys.some(k => CORE_LANG_KEYS.includes(k)) && keys.every(k => LANG_CODE_RE.test(k));
};

// Helper function to resolve localized content
const resolveLocalized = (value, language) => {
  if (value === null || value === undefined) return value;
  
  // If it's an object with language keys, return the localized value
  if (isMultilingualField(value)) {
    return value[language] || value['en'] || Object.values(value)[0] || '';
  }
  
  // If it's an array, recursively resolve each item
  if (Array.isArray(value)) {
    return value.map(item => resolveLocalized(item, language));
  }
  
  return value;
};

// Deep resolve all localized content in an object
const deepResolveLocalized = (obj, language) => {
  if (obj === null || obj === undefined) return obj;
  
  if (typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => deepResolveLocalized(item, language));
  }
  
  // Multilingual text field -> pick the current language (fallback to English)
  if (isMultilingualField(obj)) {
    return obj[language] || obj['en'] || Object.values(obj)[0] || '';
  }
  
  // Otherwise, recursively resolve nested objects
  const result = {};
  for (const key in obj) {
    // 'subtitles' maps hold media URLs keyed by language - keep them intact
    if (key === 'subtitles') {
      result[key] = obj[key];
      continue;
    }
    result[key] = deepResolveLocalized(obj[key], language);
  }
  return result;
};

export const SiteContentProvider = ({ children }) => {
  const [rawContent, setRawContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  // Fetch content from API
  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      setRawContent(response.data);
    } catch (error) {
      // Non-critical: error handling for content fetch
      if (process.env.NODE_ENV === 'development') {
        // Only log in development
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchContent();
  }, []);

  // Memoize the localized content based on language
  const siteContent = React.useMemo(() => {
    if (!rawContent) return null;
    return deepResolveLocalized(rawContent, language);
  }, [rawContent, language]);

  // Refresh content function for admin updates
  const refreshContent = fetchContent;

  // Also provide raw content for admin dashboard
  return (
    <SiteContentContext.Provider value={{ siteContent, rawContent, loading, refreshContent }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within SiteContentProvider');
  }
  return context;
};

// Export helper for use in other components
export { resolveLocalized, deepResolveLocalized };
